/**
 * /admin/tenants — read-only operator surface for tenants (OCP-23).
 *
 * Filter sidebar (status / region / pool tag / subscription state) +
 * table. Each row links through to `/admin/tenants/:id` for the
 * detail view. The tenants table is empty in prod today (no
 * provisioning flow yet); the empty-state spells out the prereq so
 * the operator knows what they're waiting on.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { RegisterSelfHostModal } from '../components/RegisterSelfHostModal';
import { ProvisionTenantModal, ProvisionTenantResultModal, type ProvisionTenantResult } from '../components/ProvisionTenantModal';
import { SelfHostSecretModal, type SelfHostSecretReveal } from '../components/SelfHostSecretModal';
import { api, ApiError } from '../lib/api';
import { formatRelative } from './HomePage';

interface TenantItem {
  id: string;
  subdomain: string;
  custom_domain: string | null;
  region: string;
  status: string;
  subscription_state: string;
  is_demo: boolean;
  is_self_host: boolean;
  self_host_url: string | null;
  license_expires_at: string | null;
  pool_tag: string;
  storage_quota_gb: number;
  origin: string;
  contact_email: string | null;
  contact_name: string | null;
  created_at: string;
  paused_at: string | null;
  /** OCP-613 - null | purging | hard_deleted; hard-deleted rows are listed only on request. */
  deletion_state?: string | null;
  /** OCP-663 - `scratch` = operator throwaway, due for hard-delete at `scratch_expires_at`. */
  purpose?: string | null;
  scratch_expires_at?: string | null;
}

interface Filters {
  status: string;
  region: string;
  pool_tag: string;
  subscription_state: string;
  /** OCP-613 - show hard-deleted tenants (archive references) too. */
  include_deleted: boolean;
}

const EMPTY_FILTERS: Filters = {
  status: '',
  region: '',
  pool_tag: '',
  subscription_state: '',
  include_deleted: false,
};

export function AdminTenantsPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [items, setItems] = useState<TenantItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoMsg, setDemoMsg] = useState<string | null>(null);
  // OCP-408 - driven by GET /v1/admin/tenants/demo, NOT the (filtered/
  // paged) list: a filter hiding the demo row must not re-enable the button.
  const [hasDemo, setHasDemo] = useState(false);
  // OCP-444 - self-host register flow: form modal + reveal-once secret modal.
  const [showSelfHostForm, setShowSelfHostForm] = useState(false);
  // OCP-663 - operator provisioning without a portal redeem.
  const [showProvisionForm, setShowProvisionForm] = useState(false);
  const [provisionResult, setProvisionResult] = useState<ProvisionTenantResult | null>(null);
  const [selfHostReveal, setSelfHostReveal] = useState<SelfHostSecretReveal | null>(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.region) params.set('region', filters.region);
    if (filters.pool_tag) params.set('pool_tag', filters.pool_tag);
    if (filters.subscription_state) params.set('subscription_state', filters.subscription_state);
    if (filters.include_deleted) params.set('include_deleted', 'true');
    params.set('limit', '200');
    return `?${params.toString()}`;
  }, [filters]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api<{ total: number; items: TenantItem[] }>(`/v1/admin/tenants${queryString}`);
      setItems(r.items);
      setTotal(r.total);
    } catch (err) {
      if (err instanceof ApiError) setError(`Could not load tenants (HTTP ${err.status}).`);
      else setError('Could not load tenants.');
    }
  }, [queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  // Filter-independent demo probe. Errors leave hasDemo=false - the
  // button stays enabled, and POSTing is idempotent server-side anyway.
  const loadDemo = useCallback(async () => {
    try {
      const r = await api<{ tenant: TenantItem | null }>('/v1/admin/tenants/demo');
      setHasDemo(r.tenant !== null);
    } catch {
      /* non-fatal - list error band covers connectivity problems */
    }
  }, []);

  useEffect(() => {
    void loadDemo();
  }, [loadDemo]);

  const provisionDemo = useCallback(async () => {
    setDemoBusy(true);
    setDemoMsg(null);
    try {
      const r = await api<{ status: string }>('/v1/admin/tenants/demo', { method: 'POST' });
      setDemoMsg(
        r.status === 'provisioning'
          ? 'Demo tenant provisioning started - it appears below once ready.'
          : r.status === 'reprovisioning'
            ? 'Stuck demo detected - provisioning re-enqueued.'
            : 'A live demo tenant already exists.',
      );
      await Promise.all([load(), loadDemo()]);
    } catch (err) {
      setDemoMsg(
        err instanceof ApiError
          ? `Demo provision failed (HTTP ${err.status}).`
          : 'Demo provision failed.',
      );
    } finally {
      setDemoBusy(false);
    }
  }, [load, loadDemo]);

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <aside className="w-full shrink-0 lg:w-56">
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Filters
            </h2>
            {hasFilters(filters) && (
              <button
                type="button"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="text-xs text-brand-500 hover:text-brand-600"
              >
                Reset
              </button>
            )}
          </div>
          <FilterInput
            label="Status"
            value={filters.status}
            placeholder="provisioning / active / paused / …"
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          />
          <FilterInput
            label="Region"
            value={filters.region}
            placeholder="eu-central-1, …"
            onChange={(v) => setFilters((f) => ({ ...f, region: v }))}
          />
          <FilterInput
            label="Pool tag"
            value={filters.pool_tag}
            placeholder="standard, …"
            onChange={(v) => setFilters((f) => ({ ...f, pool_tag: v }))}
          />
          <FilterInput
            label="Subscription state"
            value={filters.subscription_state}
            placeholder="active / past_due / …"
            onChange={(v) => setFilters((f) => ({ ...f, subscription_state: v }))}
          />
          {/* OCP-613 - hard-deleted rows are archive references, hidden by default. */}
          <label className="mt-1 flex cursor-pointer items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-brand-500"
              checked={filters.include_deleted}
              onChange={(e) => setFilters((f) => ({ ...f, include_deleted: e.target.checked }))}
            />
            Show hard-deleted tenants
          </label>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Tenants</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {items === null
                ? 'Loading…'
                : `${total} ${total === 1 ? 'tenant' : 'tenants'} matching the current filters`}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowProvisionForm(true)}
                title="Provision a managed tenant for an existing portal account (OCP-663)"
                data-testid="new-tenant-button"
                className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
              >
                New tenant
              </button>
              <button
                type="button"
                onClick={() => setShowSelfHostForm(true)}
                title="Register an existing external / self-host instance for telemetry"
                className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
              >
                Register self-host instance
              </button>
              <button
                type="button"
                onClick={() => void provisionDemo()}
                disabled={demoBusy || hasDemo}
                title={
                  hasDemo
                    ? 'A live demo tenant already exists'
                    : 'Provision the public self-healing demo tenant'
                }
                className="rounded border border-brand-300 dark:border-brand-700 bg-brand-50 dark:bg-brand-900/30 px-3 py-1.5 text-sm font-medium text-brand-700 dark:text-brand-300 hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {demoBusy ? 'Provisioning…' : hasDemo ? 'Demo tenant live' : 'Provision demo tenant'}
              </button>
            </div>
            {demoMsg && <span className="text-xs text-gray-500">{demoMsg}</span>}
          </div>
        </div>

        {showProvisionForm && (
          <ProvisionTenantModal
            onClose={() => setShowProvisionForm(false)}
            onProvisioned={(result) => {
              setShowProvisionForm(false);
              setProvisionResult(result);
              void load();
            }}
          />
        )}
        {provisionResult && (
          <ProvisionTenantResultModal result={provisionResult} onClose={() => setProvisionResult(null)} />
        )}
        {showSelfHostForm && (
          <RegisterSelfHostModal
            onClose={() => setShowSelfHostForm(false)}
            onRegistered={(reveal) => {
              setShowSelfHostForm(false);
              setSelfHostReveal(reveal);
              void load();
            }}
          />
        )}
        {selfHostReveal && (
          <SelfHostSecretModal reveal={selfHostReveal} onClose={() => setSelfHostReveal(null)} />
        )}

        {error && (
          <div
            role="alert"
            className="mb-4 flex items-center justify-between rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            <span>{error}</span>
            <button
              type="button"
              onClick={() => void load()}
              className="rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs text-red-700 dark:text-red-300 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        )}

        <TenantsTable items={items} />
      </main>
    </div>
  );
}

function hasFilters(f: Filters): boolean {
  return Boolean(f.status || f.region || f.pool_tag || f.subscription_state);
}

function FilterInput({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="mb-3 flex flex-col gap-1 text-xs">
      <span className="text-gray-600 dark:text-gray-400">{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.trim())}
        className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
      />
    </label>
  );
}

function TenantsTable({ items }: { items: TenantItem[] | null }) {
  if (items === null) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        No tenants yet. Tenants appear here once a customer redeems an
        invite code and their instance is provisioned.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">Subdomain</th>
            <th className="px-3 py-2">Region</th>
            <th className="px-3 py-2">Pool</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Subscription</th>
            <th className="px-3 py-2 text-right">Storage GB</th>
            <th className="px-3 py-2 text-right">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {items.map((it) => (
            <tr key={it.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
              <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                <Link
                  to={`/tenants/${it.id}`}
                  className="text-brand-600 hover:text-brand-700"
                >
                  {it.subdomain}
                </Link>
                {it.custom_domain && (
                  <span className="ml-2 text-xs text-gray-500">{it.custom_domain}</span>
                )}
                {it.is_demo && (
                  <span className="ml-2 inline-flex items-center rounded bg-violet-50 dark:bg-violet-900/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300 ring-1 ring-inset ring-violet-200">
                    demo
                  </span>
                )}
                {it.purpose === 'scratch' && (
                  <span
                    className="ml-2 inline-flex items-center rounded bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-200"
                    title={it.scratch_expires_at ? `Scratch tenant, due for hard-delete on ${it.scratch_expires_at.slice(0, 10)}` : 'Scratch tenant'}
                  >
                    scratch
                  </span>
                )}
                {it.is_self_host && (
                  <span
                    className="ml-2 inline-flex items-center rounded bg-sky-50 dark:bg-sky-900/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300 ring-1 ring-inset ring-sky-200"
                    title={it.self_host_url ?? 'self-host telemetry tenant'}
                  >
                    self-host
                  </span>
                )}
                {it.deletion_state === 'hard_deleted' && (
                  <span
                    className="ml-2 inline-flex items-center rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-700"
                    title="Stack, storage key, DNS and AI key are torn down; the row is the archive reference"
                    data-testid="deletion-pill"
                  >
                    hard-deleted
                  </span>
                )}
                {it.deletion_state === 'purging' && (
                  <span
                    className="ml-2 inline-flex items-center rounded bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-200"
                    title="Delete in progress - provider cleanup outstanding, the resume sweep retries"
                    data-testid="deletion-pill"
                  >
                    purging
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{it.region}</td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{it.pool_tag}</td>
              <td className="px-3 py-2">
                <StatusBadge status={it.status} />
              </td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{it.subscription_state}</td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                {it.storage_quota_gb}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                {formatRelative(it.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-emerald-200',
    provisioning: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 ring-amber-200',
    paused: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 ring-gray-200',
    decommissioned: 'bg-rose-50 text-rose-700 ring-rose-200',
  };
  const cls = styles[status] ?? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-gray-200';
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      {status}
    </span>
  );
}
